
/**
 * Module dependencies.
 */

import { Banner } from 'client/components/core/banner';
import { Container } from 'client/components/core/container';
import { DetailsLayout } from 'client/components/products/details-layout';
import { Footer } from 'client/components/core/footer';
import { GetAppModal } from 'client/components/core/get-app-modal';
import { Loading, ModalPortal } from 'slyk-web-libs/design-system';
import { MoreProducts } from 'client/components/products/more-products';
import { Navbar } from 'client/components/core/navbar';
import { Payspace, payspaceSignupModes } from 'slyk-web-libs/types';
import { Redirect, useParams } from 'react-router-dom';
import { dashboardUrlResolver } from 'client/core/utils/url-resolver';
import { formatCurrency } from 'client/core/utils/formatter';
import { gql, useQuery } from '@apollo/client';
import { ifNotProp, prop, theme } from 'styled-tools';
import { useBanner } from 'client/hooks/use-banner';
import { useBreakpoint } from 'slyk-web-libs/breakpoint';
import { usePayspace } from 'slyk-web-libs/payspace-context';
import { useQueryString } from 'client/hooks/use-query-string';
import { useSlykApp } from 'client/hooks/use-slyk-app';
import { useTranslate } from 'slyk-web-libs/i18n';
import { useWindowScroll } from 'client/hooks/use-window-scroll';
import BigNumber from 'bignumber.js';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import payspaceRoutes from 'slyk-web-libs/config/payspace-routes';
import routes from 'slyk-web-libs/config/dashboard-routes';
import styled, { css } from 'styled-components';

/**
 * Product details query.
 */

const productDetailsQuery = gql`
  query ProductDetails($id: ID!, $idFilter: FilterValueType) {
    defaultCurrency {
      assetCode: code
      decimalPlaces
      decimalPlacesToDisplay
      skipTrailingZeros
      symbol
    }

    includeTaxOnProductPrice: settingByCode(
      code: "includeTaxOnProductPrice"
    ) {
      value
    }

    product(id: $id) {
      bonus
      description
      id
      gallery {
        image {
          type
          url
        }
      }
      name
      location
      price
      priceWithTax
      taxRateId
      typeCode
      unitOfAccount
      video {
        type
        thumbnailUrl
        url
      }
    }

    products(
      filter: [{
        name: "id",
        value: $idFilter
      }, {
        name: "typeCode",
        value: "nin:fund_raise"
      }]
      page: {
        number: 1,
        size: 5
      }
      sort: "order"
    ) {
      data {
        available
        id
        location
        name
        price
        priceWithTax
        taxRateId
        thumbnail {
          ... on Image {
            status
            type
            url
          }
          ... on Video {
            status
            thumbnailUrl
            type
            url
          }
        }
        unitOfAccount
      }
      total
    }

    rewardAsset: defaultBonusAsset {
      assetCode: code
      baseRates {
        quoteAssetCode
        rate
      }
      decimalPlaces
      decimalPlacesToDisplay
      skipTrailingZeros
      symbol
      type
    }
  }
`;

/**
 * `LoadingContainer` styled component.
 */

const LoadingContainer = styled(Container)`
  display: grid;
  grid-template-rows: 1fr;
  left: 50%;
  padding-top: ${prop('navbarHeight', 122)}px;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  transition: transform ${theme('animations.defaultTransition')};
`;

/**
 * `StyledLoading` styled component.
 */

const StyledLoading = styled(Loading)`
  grid-column: 1 / -1;
  grid-row: 1;
  place-self: center;
`;

/**
 * `SectionWrapper` styled component.
 */

const SectionWrapper = styled.div<{ isVisible: boolean; navbarHeight: number }>`
  padding-top: ${prop('navbarHeight', 122)}px;

  ${ifNotProp('isVisible', css`
    display: none;
  `)}
`;

/**
 * Export `ProductDetails` container.
 */

export function ProductDetails(): JSX.Element {
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [navbarHeight, setNavbarHeight] = useState<number>(0);
  const { name, signupMode, slug } = usePayspace() as Payspace;
  const { i18n, translate } = useTranslate();
  const locale = i18n.language;
  const { id } = useParams<{ id: string }>();
  const closeModal = useCallback(() => setModalOpen(false), []);
  const { code } = useQueryString() ?? {};
  const isMobile = useBreakpoint('ms', 'max');
  const isPublicPayspace = signupMode === payspaceSignupModes.public;
  const { data, loading: isLoading } = useQuery(productDetailsQuery, {
    context: {
      headers: {
        'x-payspace-slug': slug
      }
    },
    skip: !isPublicPayspace,
    variables: {
      id,
      idFilter: `nin:${id}`
    }
  });

  const product = useMemo(() => {
    if (!data?.product) {
      return null;
    }

    const { bonus: productBonus, price, priceWithTax, taxRateId, ...rest } = data.product;
    const defaultCurrency = data?.defaultCurrency;
    const hasTaxIncluded = taxRateId && data?.includeTaxOnProductPrice?.value;
    const priceAmount = new BigNumber(hasTaxIncluded && priceWithTax ? priceWithTax : price);
    const displayValue = formatCurrency(priceAmount, { ...defaultCurrency, locale });
    const isFree = new BigNumber(priceAmount).isZero();
    const rewardAsset = data?.rewardAsset;
    const rate = rewardAsset?.baseRates?.find((rate: any) => rate?.quoteAssetCode === defaultCurrency?.assetCode);
    const hasPriceConverted = rate?.rate && price && priceAmount.isGreaterThan(0);
    const displayPriceConverted = hasPriceConverted ? formatCurrency(priceAmount.dividedBy(rate.rate), { ...rewardAsset, locale }) : null;
    const bonus = new BigNumber(productBonus);
    const bonusAmount = bonus.isGreaterThan(0) ? formatCurrency(bonus, { ...rewardAsset, locale }) : null;
    const hasBonusDefaultCurrency = rate?.rate && bonus.isGreaterThan(0);
    const bonusDefaultCurrency = hasBonusDefaultCurrency ? formatCurrency(bonus.multipliedBy(rate.rate), { ...defaultCurrency, locale }) : null;

    return {
      ...rest,
      bonus: bonusAmount,
      bonusDefaultCurrency,
      displayPrice: isFree ? translate('products.free') : displayValue,
      displayPriceConverted,
      price
    };
  }, [data, locale, translate]);

  const moreProducts = useMemo(() => {
    return data?.products?.data?.map(({
      price,
      priceWithTax,
      taxRateId,
      ...rest
    }: any) => {
      const defaultCurrency = data?.defaultCurrency;
      const hasTaxIncluded = taxRateId && data?.includeTaxOnProductPrice?.value;
      const priceAmount = new BigNumber(hasTaxIncluded && priceWithTax ? priceWithTax : price);
      const displayValue = formatCurrency(priceAmount, { ...defaultCurrency, locale });
      const isFree = new BigNumber(priceAmount).isZero();
      const rewardAsset = data?.rewardAsset;
      const rate = rewardAsset?.baseRates?.find((rate: any) => rate?.quoteAssetCode === defaultCurrency?.assetCode);
      const hasPriceConverted = rate?.rate && price && priceAmount.isGreaterThan(0);
      const displayPriceConverted = hasPriceConverted ? formatCurrency(priceAmount.dividedBy(rate.rate), { ...rewardAsset, locale }) : null;

      return {
        ...rest,
        displayPrice: isFree ? translate('products.free') : displayValue,
        displayPriceConverted,
        price
      };
    });
  }, [data, locale, translate]);

  const {
    canDownload,
    downloadAppUrl,
    getSlykApp
  } = useSlykApp({
    code: code as string | null | undefined,
    onOpenModal: () => setModalOpen(true),
    slug
  });

  const { isNavbarFixed } = useWindowScroll();
  const { isBannerVisible, onClose } = useBanner();

  useEffect(() => {
    function handleResize() {
      const navbarHeight = document.querySelector('nav')?.offsetHeight ?? 0;

      setNavbarHeight(navbarHeight);
    }

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!isLoading && !product) {
    return <Redirect to={payspaceRoutes.products.base} />;
  }

  return (
    <>
      <Banner
        goToDashboard={dashboardUrlResolver(slug, routes.overview)}
        isVisible={isBannerVisible}
        onDismiss={onClose}
      />

      <Navbar
        downloadAppDisabled={isLoading}
        isFixed={isNavbarFixed}
        onGetApp={getSlykApp}
        showBadges={!isMobile}
      />

      {!!isLoading && (
        <LoadingContainer navbarHeight={navbarHeight}>
          <StyledLoading
            active
            relative
          />
        </LoadingContainer>
      )}

      <SectionWrapper
        isVisible={!isLoading}
        navbarHeight={navbarHeight}
      >
        {!!product && (
          <DetailsLayout
            getSlykApp={getSlykApp}
            product={product}
          />
        )}

        <MoreProducts products={moreProducts} />

        <Footer
          getSlykApp={getSlykApp}
          payspaceName={name}
          payspaceSlug={slug}
        />
      </SectionWrapper>

      {!canDownload && (
        <ModalPortal isOpen={isModalOpen}>
          <GetAppModal
            code={code as string | null | undefined}
            downloadAppUrl={downloadAppUrl}
            isVisible={isModalOpen}
            onRequestClose={closeModal}
          />
        </ModalPortal>
      )}
    </>
  );
}
